// @flow

import React from "react";
import { localize } from "redux-i18n";

import actionCreators from "@modules/service/actionCreator";
import { Button, GSGrid, ComboBox, Text, View } from "@components";
import type { OptionType } from "@components";
import type { RtwAbsenceReason } from "@modules/service/reducer/rtw/types";

type OwnProps = $Exact<{
  data: ?Array<RtwAbsenceReason>,
  schema?: Object | null,
  onChange?: Function,
  onSubmit?: Function,
  editing?: boolean,
  disabled?: boolean,
  disabledReasonAdding?: boolean,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

type AbsenceRow = {
  id: number,
  revisionId: number,
  label: string,
  isActive: boolean,
};

export class RtwReasonScope extends React.Component<Props> {
  get defaultReason() {
    return [
      {
        id: new Date().getTime(),
        revisionId: 1,
        label: "",
        isActive: true,
      },
    ];
  }

  get data() {
    return this.props.data || [];
  }

  get schemaOptions() {
    return this.props.schema.properties?.id?.oneOf || [];
  }

  get options(): { value: number, label: string }[] {
    const options = this.props.schema.properties?.id?.oneOf || [];
    return options
      .filter(opt => !this.data.find(el => el.id === opt.const))
      .map(el => ({ value: el.const, label: el.title }));
  }

  get columns() {
    return [
      {
        name: this.props.t("ABSENCE_REASONS"),
        key: "label",
        align: "left",
        maxWidth: "100%",
        renderCell: ({ row }: { row: AbsenceRow }) => {
          return (
            <View direction="row" justifyContent="space-between" flex={1}>
              <View className={!row.isActive ? "text-color-negative" : ""}>{row.label}</View>
              {!row.isActive && (
                <Text color="negative">{this.props.t("INACTIVE_REASON_LABEL")}</Text>
              )}
            </View>
          );
        },
      },
    ];
  }

  handleChange = (data: number[]) => {
    const dataToUpdate = data.map(el => {
      const opt = this.multiOptions.find(opt => opt.value === el) || {};

      return {
        id: el,
        revisionId: 1,
        label: opt.label,
        isActive: true,
      };
    });

    this.props.onChange({
      absenceReasons: dataToUpdate,
    });
  };

  get selected() {
    return this.data.map(el => ({ value: el.id, label: el.label }));
  }

  get inactiveSelectedReasons() {
    const inactiveValues = this.selected
      .filter(el => !this.schemaOptions.find(opt => opt.const === el.value))
      .map(el => ({ ...el, warning: this.props.t("INACTIVE_REASON_LABEL") }));

    return inactiveValues;
  }

  get multiOptions() {
    const options = this.props.schema.properties?.id?.oneOf || [];

    const formattedOption = this.schemaOptions.map(el => ({ value: el.const, label: el.title }));
    return [...this.inactiveSelectedReasons, ...formattedOption];
  }

  render() {
    if (!this.props.isEdit && !this.data.length) {
      return null;
    }

    return (
      <View margin="2rem 0" alignItems="center">
        {this.props.isEdit ? (
          <>
            {!!this.inactiveSelectedReasons.length && (
              <Text color="negative" lineHeight={5}>
                {this.props.t("INACITIVE_REASONS_WARNING")}
              </Text>
            )}
            <ComboBox
              value={this.selected}
              options={this.multiOptions}
              onChange={this.handleChange}
              className="w-100"
              isMulti
            />
          </>
        ) : (
          <View flex={1} width="100%">
            <GSGrid columns={this.columns} rows={this.data} />
          </View>
        )}
      </View>
    );
  }
}

export default localize()(RtwReasonScope);
